<script setup>
// Astra DS · NavItem — celda de navegación de la cabecera.
//
// Figma: NavItem 668:657 (átomo).
//
// ── El estado se dice con color, y solo con color ──
//
// 🔴 **Aquí había una regla de 2 px al pie del activo** (`rule 668:655`). Se
// retira el 07.09.26: con el icono en acento serían dos marcas para el mismo
// estado. Es la decisión que la app tomó el 06.09 en su `TabBar`, así que las
// dos superficies dicen «estás dentro» igual.
//
//   reposo  texto `secondary` · icono `tertiary`
//   hover   texto `primary`   · icono `primary`
//   activo  texto `primary`   · icono `accent`
//
// El icono y la palabra **no van del mismo color en el mismo estado**, así que
// el icono no puede heredar del enlace: lleva el suyo.
//
// ⚠️ El hover **no rellena**. El fondo es el recurso con el que este sistema
// construye profundidad —`canvas` → `elevated` → `subtle` → `strong`— y el
// hover no es profundidad, es atención. Tampoco lleva acento: el acento
// significa «estás dentro», y si dijera también «estás encima» dejaría de
// decir las dos cosas. (DESIGN.md, entrega del 07.09.26.)
import AstraIcon from "@/components/icons/AstraIcon.vue";

defineProps({
  label: { type: String, required: true },
  // Nombre en `AstraIcon`. Sin él la celda es solo palabra, que es como vivió
  // hasta el 07.09.26.
  icono: { type: String, default: "" },
  to: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  // `State=destacado` `2396:371` (06.10.26): la celda de Donar. El icono y la
  // palabra van dentro de una caja de 28 con filete `border/accent`. Es la única
  // celda destacada de la navegación; no se usa para ningún otro destino.
  destacado: { type: Boolean, default: false },
});
</script>

<template>
  <component
    :is="to ? 'router-link' : 'button'"
    :to="to || undefined"
    :type="to ? null : 'button'"
    class="nav-item"
    :class="{ 'is-active': active, 'nav-item--destacado': destacado }"
    :aria-current="active ? 'page' : null"
  >
    <span v-if="destacado" class="nav-item__caja">
      <AstraIcon v-if="icono" class="nav-item__icono" :name="icono" :size="15" />
      <span class="nav-item__label">{{ label }}</span>
    </span>
    <template v-else>
      <AstraIcon v-if="icono" class="nav-item__icono" :name="icono" :size="15" />
      <span class="nav-item__label">{{ label }}</span>
    </template>
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.nav-item {
  position: relative;
  display: flex;
  flex: none;
  // 8, que está en la escala. La referencia traía 9 y 17, que no lo están.
  gap: 8px;
  align-items: center;
  justify-content: center;
  // `align-self: stretch` y no `height: 100%`. El porcentaje resuelve contra la
  // caja de contenido del padre, así que cualquier relleno heredado lo encoge;
  // stretch estira contra la línea flex y no depende de nada de fuera.
  align-self: stretch;
  padding: 0 16px;
  border: 0;
  border-right: 1px solid var(--color-border-default);
  border-radius: 0;
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.12s linear;

  &:hover {
    color: var(--color-text-primary);

    .nav-item__icono {
      color: var(--color-icon-primary);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }

  &.is-active {
    color: var(--color-text-primary);

    .nav-item__icono {
      color: var(--color-icon-accent);
    }
  }
}

.nav-item__icono {
  color: var(--color-icon-tertiary);
  transition: color 0.12s linear;
}

.nav-item__label {
  @include t.mono-label;
}

// ── `State=destacado` · la celda de Donar ──
//
// La celda pasa de 16 a 9 de relleno lateral para que la caja no la ensanche de
// más, y dentro va la caja: 28 de alto, 12 a los lados, 8 de hueco, filete
// `border/accent` a los cuatro lados, sin fondo y sin radio. La palabra en
// primario y el corazón en acento, en reposo y dentro de `/donar` igual: el
// estado activo propio está pendiente de diseño.
//
// 🔴 **El hover marca el filete, no rellena** (§ El hover marca el filete): la
// caja baja a `action/primary/hover` y nada más cambia.
.nav-item--destacado {
  padding: 0 9px;
  color: var(--color-text-primary);

  .nav-item__icono {
    color: var(--color-icon-accent);
  }

  &:hover,
  &.is-active {
    color: var(--color-text-primary);

    .nav-item__icono {
      color: var(--color-icon-accent);
    }
  }

  &:hover .nav-item__caja {
    border-color: var(--color-action-primary-hover);
  }
}

.nav-item__caja {
  display: flex;
  gap: 8px;
  align-items: center;
  box-sizing: border-box;
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--color-border-accent);
  transition: border-color 0.12s linear;
}
</style>
